import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import Modal from "../../../components/base/Modal";
import ApiCrudSection, {
  type Column,
  type FormField,
  type Row,
} from "../components/ApiCrudSection";
import {
  fetchPayslips,
  createPayslip,
  deletePayslip,
  approvePayslip,
  stampPayslip,
  fetchPeriods,
} from "../services/payrollService";
import {
  payslipStatusLabel,
  PAYSLIP_STATUS_TONES,
  stampStatusLabel,
  STAMP_STATUS_TONES,
  paymentStatusLabel,
  PAYMENT_STATUS_TONES,
  noCalcReasonLabel,
  buildThemeClasses,
  formatAmount,
  formatDays,
} from "../theme";
import type { Payslip, PayrollPeriod } from "../types";
import KpiStrip, { type KpiItem } from "../../../components/staff-ops/KpiStrip";
import PeriodTimeline from "../../../components/staff-ops/PeriodTimeline";
import { compactMoney } from "../../../components/staff-ops/relative";
import { periodStatusLabel } from "../theme";
import { useIsStaffAdmin } from "../../../hooks/useIsStaffAdmin";

// ---------------------------------------------------------------------------
// Liquidación de sueldo (AgroCloud#38) + su comprobante fiscal (AgroCloud#82).
//
// La liquidación se genera desde lo ya capturado: la asistencia del periodo
// (AgroCloud#36), las incidencias AUTORIZADAS (AgroCloud#81) y los anticipos/haberes pendientes
// (AgroCloud#37), más lo previsional. El usuario no teclea importes: elige colaborador y
// periodo, y el backend arma el detalle.
//
// ---------------------------------------------------------------------------
// LO QUE ESTA PANTALLA NO HACE, A PROPÓSITO
// ---------------------------------------------------------------------------
// • NO calcula el pago de la tarea: esa fórmula vive en el módulo Equipo
//   (AgroCloud#13 / PR AgroCloud#100) y el backend la delega. Mientras ese módulo no esté
//   desplegado, el sueldo por jornadas NO se puede calcular y la liquidación
//   aparece con líquido "—" y el badge "Requiere módulo Equipo". No un 0.
//
// • NO muestra un líquido cuando algún importe es desconocido. Un "—" incómodo
//   es infinitamente mejor que una cifra falsa: alguien va a cobrar esto.
//
// • NO timbra de mentira (AgroCloud#82). Sin PAC contratado, el botón de timbrar
//   devuelve "PAC no configurado" y lo dice en ámbar, con el porqué. No hay
//   UUID inventado: un CFDI falso es un delito fiscal, no un placeholder.
// ---------------------------------------------------------------------------

export default function PayslipsSection() {
  // #1007: solo quien administra modifica; los demás ven lo suyo en solo lectura.
  const isStaffAdmin = useIsStaffAdmin("payroll");
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);

  const [rows, setRows] = useState<Payslip[]>([]);
  const [periods, setPeriods] = useState<PayrollPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [detail, setDetail] = useState<Payslip | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pay, period] = await Promise.all([
        fetchPayslips(),
        fetchPeriods().catch(() => ({ items: [] as PayrollPeriod[] })),
      ]);
      setRows(pay.items);
      setPeriods(period.items);
    } catch {
      setError(t("payroll.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleApprove = async (id: string) => {
    setActionError(null);
    try {
      await approvePayslip(id);
      await load();
    } catch {
      // El backend bloquea las incompletas: no se aprueba un sueldo desconocido.
      setActionError(t("payroll.approveError"));
    }
  };

  const handleStamp = async (id: string) => {
    setActionError(null);
    try {
      const res = await stampPayslip(id);
      await load();
      // Sin PAC esto NO es un fallo del sistema: es su estado real, y se dice.
      if (res?.stamped?.status !== "stamped") {
        setActionError(res?.stamped?.error ?? t("payroll.stampError"));
      }
    } catch {
      setActionError(t("payroll.stampError"));
    }
  };

  // #1595: línea de tiempo de los periodos e indicadores; elegir un periodo
  // acota la tabla a sus recibos.
  const [periodFilter, setPeriodFilter] = useState<string | null>(null);
  const todayIso = new Date().toISOString().slice(0, 10);
  // Solo suman los recibos completos: un líquido desconocido no es 0 (AgroCloud#38).
  const completeOf = (list: Payslip[]) => list.filter((p) => p.calculation?.complete !== false && p.calculation?.netPay != null);
  const netOf = (list: Payslip[]) => completeOf(list).reduce((n, p) => n + (Number(p.calculation?.netPay) || 0), 0);
  const timeline = useMemo(
    () =>
      periods.map((p) => {
        const mine = rows.filter((r) => r.periodId === p.id && r.status !== "void");
        return {
          id: p.id,
          name: p.name,
          startDate: p.startDate,
          endDate: p.endDate,
          payDate: p.payDate ?? null,
          status: p.status ?? "open",
          payslips: mine.length,
          net: completeOf(mine).length ? compactMoney(netOf(mine), p.currency ?? "MXN") : null,
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [periods, rows],
  );
  const live = rows.filter((r) => r.status !== "void");
  const kpis: KpiItem[] = [
    { id: "open", label: t("staffOps.payroll.openPeriods"), value: periods.filter((p) => (p.status ?? "open") === "open").length, icon: "ri-calendar-todo-line", tone: "primary", hint: t("staffOps.payroll.periodsTotal", { count: periods.length }) },
    { id: "review", label: t("staffOps.payroll.inReview"), value: live.filter((r) => r.status === "calculated" || r.status === "draft").length, icon: "ri-file-search-line", tone: "warning" },
    { id: "approved", label: t("staffOps.payroll.approved"), value: live.filter((r) => r.status === "approved").length, icon: "ri-checkbox-circle-line", tone: "info" },
    { id: "paid", label: t("staffOps.payroll.paid"), value: live.filter((r) => r.status === "paid").length, icon: "ri-bank-card-line", tone: "success" },
    { id: "incomplete", label: t("staffOps.payroll.incomplete"), value: live.filter((r) => r.calculation && r.calculation.complete === false).length, icon: "ri-error-warning-line", tone: "error", hint: t("staffOps.payroll.incompleteHint") },
    {
      id: "net",
      label: t("staffOps.payroll.netToPay"),
      value: (() => {
        const payable = completeOf(live.filter((r) => r.status === "calculated" || r.status === "approved"));
        return payable.length ? compactMoney(netOf(payable), "MXN") : "—";
      })(),
      icon: "ri-money-dollar-circle-line",
      tone: "violet",
      hint: t("staffOps.payroll.netHint"),
    },
  ];
  const shownRows = periodFilter ? rows.filter((r) => r.periodId === periodFilter) : rows;
  const opsBanner = (
    <div className="mb-4 space-y-4">
      <KpiStrip items={kpis} loading={loading} />
      <PeriodTimeline
        periods={timeline}
        today={todayIso}
        title={t("staffOps.timeline.title")}
        todayLabel={t("staffOps.timeline.today")}
        payDayLabel={t("staffOps.timeline.payDay")}
        statusLabel={(st) => periodStatusLabel(t, st)}
        payslipsLabel={(count) => t("staffOps.timeline.payslips", { count })}
        emptyLabel={t("staffOps.timeline.empty")}
        selectedId={periodFilter}
        onSelect={(id) => setPeriodFilter((cur) => (cur === id ? null : id))}
      />
    </div>
  );

  const badge = (tone: string, label: string) => (
    <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${tone}`}>{label}</span>
  );

  const columns: Column[] = [
    {
      key: "teamMemberName",
      label: t("payroll.fields.teamMember"),
      render: (row) => String(row.teamMemberName || row.teamMemberId || "—"),
    },
    {
      key: "periodName",
      label: t("payroll.fields.period"),
      render: (row) => String(row.periodName || "—"),
    },
    {
      key: "days",
      label: t("payroll.fields.days"),
      align: "right",
      render: (row) => {
        const calculation = row.calculation as Payslip["calculation"];
        return formatDays(calculation?.daysWorked);
      },
    },
    {
      key: "totalEarnings",
      label: t("payroll.fields.totalEarnings"),
      align: "right",
      render: (row) => {
        const calculation = row.calculation as Payslip["calculation"];
        return formatAmount(calculation?.totalEarnings, String(row.currency || "MXN"));
      },
    },
    {
      key: "totalDeductions",
      label: t("payroll.fields.totalDeductions"),
      align: "right",
      render: (row) => {
        const calculation = row.calculation as Payslip["calculation"];
        return formatAmount(calculation?.totalDeductions, String(row.currency || "MXN"));
      },
    },
    {
      key: "netPay",
      label: t("payroll.fields.netPay"),
      align: "right",
      render: (row) => {
        const calculation = row.calculation as Payslip["calculation"];
        // "—" cuando no se sabe. NUNCA 0: es el sueldo de una persona.
        return (
          <span className={`font-semibold ${calculation?.complete ? "text-emerald-500" : ""}`}>
            {formatAmount(calculation?.netPay, String(row.currency || "MXN"))}
          </span>
        );
      },
    },
    {
      key: "paymentStatus",
      label: t("payroll.fields.calculation"),
      render: (row) => {
        const calculation = row.calculation as Payslip["calculation"];
        const status = calculation?.paymentStatus ?? "ok";
        if (calculation?.complete) {
          return badge(PAYMENT_STATUS_TONES.ok, paymentStatusLabel(t, "ok"));
        }
        return badge(
          PAYMENT_STATUS_TONES[status] ?? "bg-amber-500/15 text-amber-500",
          paymentStatusLabel(t, status),
        );
      },
    },
    {
      key: "status",
      label: t("payroll.fields.payslipStatus"),
      render: (row) => {
        const status = String(row.status || "draft");
        return badge(
          PAYSLIP_STATUS_TONES[status] ?? "bg-gray-400/15 text-gray-500",
          payslipStatusLabel(t, status),
        );
      },
    },
    {
      key: "stamped",
      label: t("payroll.fields.stamped"),
      render: (row) => {
        const stamped = row.stamped as Payslip["stamped"];
        const status = stamped?.status ?? "not_stamped";
        return badge(
          STAMP_STATUS_TONES[status] ?? "bg-gray-400/15 text-gray-500",
          stampStatusLabel(t, status),
        );
      },
    },
  ];

  // El alta NO pide importes: se generan desde lo capturado y autorizado.
  const fields: FormField[] = [
    { key: "teamMemberId", label: t("payroll.fields.teamMemberId"), required: true },
    { key: "teamMemberName", label: t("payroll.fields.teamMemberName") },
    {
      // Obligatorio: el backend rechaza con 400 una liquidación sin periodo.
      // Presentarlo como opcional sólo servía para que el usuario recibiera un
      // error del servidor por un campo que la pantalla decía que podía dejar vacío.
      key: "periodId",
      label: t("payroll.fields.period"),
      type: "select",
      required: true,
      hint: t("payroll.hints.payslipPeriod"),
      options: periods
        .filter((p) => p.status !== "closed")
        .map((p) => ({ value: p.id, label: p.name })),
    },
    {
      // La tarifa diaria SÓLO manda en la modalidad por día: un colaborador
      // por hora o de sueldo fijo se calcula con su tarifa/sueldo, y este valor
      // se guardaría sin que el cálculo lo mire. El campo lo dice en su nombre
      // para que nadie crea que teclear aquí cambia ese sueldo.
      key: "dailyRate",
      label: t("payroll.fields.dailyRateDailyMode"),
      type: "number",
      hint: t("payroll.hints.dailyRate"),
    },
    { key: "notes", label: t("payroll.fields.notes"), type: "textarea" },
  ];

  const extraActions = (row: Row) => {
    const calculation = row.calculation as Payslip["calculation"];
    const status = String(row.status || "draft");
    const stamped = row.stamped as Payslip["stamped"];
    return (
      <>
        <button
          title={t("payroll.viewDetail")}
          aria-label={`detail-${row.id}`}
          onClick={() => setDetail(row as unknown as Payslip)}
          className="rounded-md p-1.5 text-primary-500 hover:bg-primary-500/10"
        >
          <i className="ri-eye-line" />
        </button>
        {isStaffAdmin && status === "calculated" && calculation?.complete && (
          <button
            title={t("payroll.approve")}
            aria-label={`approve-${row.id}`}
            onClick={() => handleApprove(row.id)}
            className="rounded-md p-1.5 text-emerald-500 hover:bg-emerald-500/10"
          >
            <i className="ri-check-double-line" />
          </button>
        )}
        {isStaffAdmin &&
          (status === "approved" || status === "paid") &&
          stamped?.status !== "stamped" && (
            <button
              title={t("payroll.stamp")}
              aria-label={`stamp-${row.id}`}
              onClick={() => handleStamp(row.id)}
              className="rounded-md p-1.5 text-sky-500 hover:bg-sky-500/10"
            >
              <i className="ri-government-line" />
            </button>
          )}
      </>
    );
  };

  const hasIncomplete = rows.some((l) => l.calculation && l.calculation.complete === false);

  const banner = (
    <div className="mb-4 space-y-3">
      {opsBanner}
      {/* La verdad sobre el timbrado, siempre visible (#82). */}
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-500">
        <i className="ri-government-line mr-1" />
        {t("payroll.stampingNotice")}
      </div>

      {/* Y la verdad sobre el cálculo del sueldo mientras falte #100. */}
      {hasIncomplete && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-500">
          <i className="ri-error-warning-line mr-1" />
          {t("payroll.incompleteNotice")}
        </div>
      )}

      {actionError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-500">
          <i className="ri-error-warning-line mr-1" />
          {actionError}
        </div>
      )}
    </div>
  );

  const handleCreate = async (values: Record<string, string>) => {
    await createPayslip({
      teamMemberId: values.teamMemberId,
      teamMemberName: values.teamMemberName || null,
      periodId: values.periodId || null,
      dailyRate:
        String(values.dailyRate ?? "").trim() === "" ? null : Number(values.dailyRate),
      notes: values.notes || null,
    });
    await load();
  };

  const handleDelete = async (id: string) => {
    await deletePayslip(id);
    await load();
  };

  /** Comprobante de liquidación por colaborador: el detalle línea a línea (AgroCloud#38). */
  const detailContent = detail && (
    <div data-testid="payroll-detail" className="space-y-4">
      <div>
        <p className={`text-sm font-semibold ${tc.text}`}>
          {detail.teamMemberName || detail.teamMemberId}
        </p>
        <p className={`text-xs ${tc.textSecondary}`}>{detail.periodName || "—"}</p>
      </div>

      {detail.calculation?.complete === false && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-500">
          <i className="ri-error-warning-line mr-1" />
          {t("payroll.incompleteDetail")}
        </div>
      )}

      <div className="w-full overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-primary-500 text-white">
            <tr>
              <th className="p-2 text-left font-bold">{t("payroll.fields.concept")}</th>
              <th className="p-2 text-left font-bold">{t("payroll.fields.lineSource")}</th>
              <th className="p-2 text-right font-bold">{t("payroll.fields.earning")}</th>
              <th className="p-2 text-right font-bold">{t("payroll.fields.deduction")}</th>
            </tr>
          </thead>
          <tbody>
            {(detail.calculation?.lines ?? []).map((line) => (
              <tr key={line.id} className={isDarkMode ? "text-white" : "text-gray-900"}>
                <td className="p-2">
                  {line.concept}
                  {line.amount === null && (
                    <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-500">
                      {paymentStatusLabel(t, line.status ?? "ok")}
                    </span>
                  )}
                  {/* Motivo exacto de #100 (p. ej. "la tarifa no cubre ese
                      rendimiento"): el usuario necesita saber qué corregir. */}
                  {line.amount === null && line.noCalcReason && (
                    <p className="mt-0.5 text-[10px] text-amber-500">
                      {noCalcReasonLabel(t, line.noCalcReason)}
                    </p>
                  )}
                </td>
                <td className={`p-2 ${tc.textSecondary}`}>{line.origin}</td>
                <td className="p-2 text-right">
                  {line.type === "earning"
                    ? formatAmount(line.amount, detail.currency ?? "MXN")
                    : ""}
                </td>
                <td className="p-2 text-right">
                  {line.type === "deduction"
                    ? formatAmount(line.amount, detail.currency ?? "MXN")
                    : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <dl className={`space-y-1 border-t pt-3 text-sm ${tc.border}`}>
        <div className="flex justify-between">
          <dt className={tc.textSecondary}>{t("payroll.fields.totalEarnings")}</dt>
          <dd className={tc.text}>
            {formatAmount(detail.calculation?.totalEarnings, detail.currency ?? "MXN")}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className={tc.textSecondary}>{t("payroll.fields.taxableTotal")}</dt>
          <dd className={tc.text}>
            {formatAmount(detail.calculation?.taxableTotal, detail.currency ?? "MXN")}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className={tc.textSecondary}>{t("payroll.fields.totalDeductions")}</dt>
          <dd className={tc.text}>
            {formatAmount(detail.calculation?.totalDeductions, detail.currency ?? "MXN")}
          </dd>
        </div>
        <div className="flex justify-between font-semibold">
          <dt className={tc.text}>{t("payroll.fields.netPay")}</dt>
          <dd className={detail.calculation?.complete ? "text-emerald-500" : "text-amber-500"}>
            {formatAmount(detail.calculation?.netPay, detail.currency ?? "MXN")}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className={tc.textSecondary}>{t("payroll.fields.employerContribution")}</dt>
          <dd className={tc.textSecondary}>
            {formatAmount(detail.calculation?.totalEmployerContribution, detail.currency ?? "MXN")}
          </dd>
        </div>
      </dl>

      {detail.stamped && detail.stamped.status !== "not_stamped" && (
        <div className={`rounded-lg border p-3 text-xs ${tc.border}`}>
          <p className={`font-semibold ${tc.text}`}>{t("payroll.fields.stamped")}</p>
          <p className={tc.textSecondary}>
            {stampStatusLabel(t, detail.stamped.status)}
          </p>
          {detail.stamped.uuid && (
            <p className={tc.textSecondary}>UUID: {detail.stamped.uuid}</p>
          )}
          {detail.stamped.error && (
            <p className="mt-1 text-amber-500">{detail.stamped.error}</p>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <ApiCrudSection
        readOnly={!isStaffAdmin}
        title={t("payroll.menu.payslips")}
        subtitle={t("payroll.payslipsSubtitle")}
        icon="ri-file-list-3-line"
        addLabel={t("payroll.newPayslip")}
        columns={columns}
        fields={fields}
        rows={shownRows as unknown as Row[]}
        loading={loading}
        error={error}
        searchKey="teamMemberName"
        banner={banner}
        extraActions={extraActions}
        onCreate={handleCreate}
        onDelete={handleDelete}
      />

      {detail && (
        <Modal
          title={t("payroll.receiptTitle")}
          onClose={() => setDetail(null)}
          themeClasses={tc}
          width="max-w-3xl"
          content={detailContent}
        />
      )}
    </>
  );
}
